// The End generator (DIM-3). PURE + deterministic (ENG-1/ENG-3): same seed +
// config yields identical End chunks on the main thread, in the gen worker, and on
// the server.
//
// The End is mostly void. Three things exist in it:
//
//  1. The **central island** — a single end-stone disc centred on the origin, its
//     thickness a smooth radial falloff (thick and domed at the middle, tapering to
//     a knife edge at the rim) roughened by a low-amplitude noise so it isn't a
//     perfect cylinder.
//  2. The **obsidian pillars** ringing that island — a fixed circle of towers at
//     deterministic radii/heights, each capped with an end crystal (the dragon
//     fight's healers).
//  3. The **outer islands** — beyond a wide empty gap, sparse floating end-stone
//     shards carved from 3D noise, thinning with distance.
//
// Everything else is air, and the dimension has no bedrock shell, no sea and no
// sky light source — falling off an edge is the hazard.
import { WORLD, END } from '../../config';
import { STATE_AIR } from '../../engine/section';
import { Rng, defineSalt } from '../../engine/rng';
import { Simplex, fbm2, fbm3 } from '../../engine/noise';

/** Block-state ids the End generator places. */
export interface EndIds {
  endStone: number;
  obsidian: number;
  /** End crystal atop each pillar; omit to leave the pillars bare. */
  endCrystal?: number;
  /** Chorus stalk + its crowning bloom; omit both to leave the islands bare. */
  chorusPlant?: number;
  chorusFlower?: number;
}

/** A place-cell sink (local x/z 0..15, world Y). */
export type SetCell = (lx: number, y: number, lz: number, state: number) => void;

const S_SHAPE = defineSalt('end-island-shape');
const S_OUTER = defineSalt('end-outer-islands');
const S_PILLAR = defineSalt('end-pillars');
const S_CHORUS = defineSalt('end-chorus');

/** One obsidian pillar of the central ring. */
export interface EndPillar {
  x: number;
  z: number;
  /** Top Y of the obsidian column (the crystal sits one above). */
  topY: number;
  radius: number;
}

/**
 * The pillar ring, derived from the seed alone (no chunk state) so every caller —
 * generator, dragon fight, `/locate` — agrees on where the towers are.
 */
export function endPillars(seed: number | string | bigint): EndPillar[] {
  const rng = new Rng(`${seed}#${S_PILLAR}`);
  const out: EndPillar[] = [];
  for (let i = 0; i < END.PILLAR_COUNT; i++) {
    const a = (i / END.PILLAR_COUNT) * Math.PI * 2;
    const dist = END.PILLAR_RING_RADIUS + rng.nextInt(END.PILLAR_RING_JITTER * 2 + 1) - END.PILLAR_RING_JITTER;
    out.push({
      x: Math.round(Math.cos(a) * dist),
      z: Math.round(Math.sin(a) * dist),
      topY: END.ISLAND_TOP + END.PILLAR_MIN_H + rng.nextInt(END.PILLAR_MAX_H - END.PILLAR_MIN_H + 1),
      radius: END.PILLAR_MIN_R + rng.nextInt(END.PILLAR_MAX_R - END.PILLAR_MIN_R + 1),
    });
  }
  return out;
}

export class EndTerrain {
  private readonly shape: Simplex;
  private readonly outer: Simplex;
  private readonly pillars: EndPillar[];
  private readonly chorusRng: Rng;

  constructor(seed: number | string | bigint, private readonly ids: EndIds) {
    this.shape = new Simplex(`${seed}#${S_SHAPE}`);
    this.outer = new Simplex(`${seed}#${S_OUTER}`);
    this.pillars = endPillars(seed);
    this.chorusRng = new Rng(seed).fork(S_CHORUS);
  }

  /** The ring of obsidian pillars (shared with the dragon fight). */
  pillarRing(): readonly EndPillar[] { return this.pillars; }

  /**
   * Half-thickness of the central island at (x,z), or 0 when outside it. The disc
   * is domed: full thickness at the centre, tapering with a smooth (1−r²) falloff,
   * plus a small noise wobble on the rim so the edge is ragged, not circular.
   */
  private centralThickness(x: number, z: number): number {
    const r = Math.hypot(x, z);
    const wobble = fbm2(this.shape, x, z, { frequency: END.SHAPE_FREQ, octaves: 2 }) * END.SHAPE_AMP;
    const edge = END.ISLAND_RADIUS + wobble;
    if (r >= edge) return 0;
    const t = 1 - (r / edge) ** 2;
    return Math.max(1, Math.round(END.ISLAND_THICKNESS * t));
  }

  /** Is (x,y,z) inside an outer-island shard? Beyond the void gap only. */
  private inOuterIsland(x: number, y: number, z: number): boolean {
    const r = Math.hypot(x, z);
    if (r < END.OUTER_START) return false;
    if (y < END.OUTER_MIN_Y || y > END.OUTER_MAX_Y) return false;
    // Shards thin out with distance: the density threshold rises as r grows.
    const fade = Math.min(1, (r - END.OUTER_START) / END.OUTER_FADE);
    const thresh = END.OUTER_THRESH + fade * END.OUTER_THRESH_FADE;
    // A vertical squash keeps shards flat-ish (islands, not blobs).
    const yy = (y - END.OUTER_CENTRE_Y) * END.OUTER_Y_SQUASH + END.OUTER_CENTRE_Y;
    return fbm3(this.outer, x, yy, z, { frequency: END.OUTER_FREQ, octaves: 3 }) > thresh;
  }

  /**
   * The End's three biomes, decided purely by what generated in a column:
   * `the_end` = the central island (the dragon arena), `end_highlands` = an outer
   * island shard, `the_void` = nothing at all. This is what keeps end cities off
   * the arena and out of the void — a void column reports the same fallback
   * surface height as the flat arena, so the flatness predicate alone can't tell
   * them apart, but the biome can.
   */
  biomeAt(x: number, z: number): 'the_end' | 'end_highlands' | 'the_void' {
    if (this.centralThickness(x, z) > 0) return 'the_end';
    for (let y = END.OUTER_MAX_Y; y >= END.OUTER_MIN_Y; y--) {
      if (this.inOuterIsland(x, y, z)) return 'end_highlands';
    }
    return 'the_void';
  }

  /** Surface Y at a column (spawn placement); END.ISLAND_TOP when in the void. */
  surfaceY(x: number, z: number): number {
    const t = this.centralThickness(x, z);
    if (t > 0) return END.ISLAND_TOP;
    for (let y = END.OUTER_MAX_Y; y >= END.OUTER_MIN_Y; y--) if (this.inOuterIsland(x, y, z)) return y;
    return END.ISLAND_TOP;
  }

  /** Fill one End chunk: central island, pillars, outer shards. */
  fill(cx: number, cz: number, set: SetCell): void {
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        const wx = bx + lx, wz = bz + lz;
        const t = this.centralThickness(wx, wz);
        for (let y = END.ISLAND_TOP - t + 1; y <= END.ISLAND_TOP; y++) set(lx, y, lz, this.ids.endStone);
        if (t === 0) {
          for (let y = END.OUTER_MIN_Y; y <= END.OUTER_MAX_Y; y++) {
            if (this.inOuterIsland(wx, y, wz)) set(lx, y, lz, this.ids.endStone);
          }
        }
      }
    }
    this.raisePillars(cx, cz, set);
    this.growChorus(cx, cz, set);
  }

  /**
   * Chorus plants on the OUTER islands only (the central island stays the bare
   * arena the dragon is fought over, as in Minecraft). Each is a knobbly stalk of a
   * few blocks, sometimes forking once, crowned with a chorus flower.
   *
   * The stalk is written blind rather than probed: `fill` has no read accessor, and
   * a chorus grows out of the island top into open void, so there is nothing above
   * it to collide with.
   */
  private growChorus(cx: number, cz: number, set: SetCell): void {
    const { chorusPlant, chorusFlower } = this.ids;
    if (chorusPlant === undefined || chorusFlower === undefined) return;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    if (Math.hypot(bx, bz) < END.OUTER_START) return; // central island stays bare
    const rng = this.chorusRng.forkAt(S_CHORUS, cx, cz);
    for (let t = 0; t < END.CHORUS_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const wx = bx + lx, wz = bz + lz;
      // Find the island top in this column (highest shard cell).
      let top = -1;
      for (let y = END.OUTER_MAX_Y; y >= END.OUTER_MIN_Y; y--) {
        if (this.inOuterIsland(wx, y, wz)) { top = y; break; }
      }
      if (top < 0) continue;
      const h = END.CHORUS_MIN_H + rng.nextInt(END.CHORUS_MAX_H - END.CHORUS_MIN_H + 1);
      for (let i = 1; i <= h; i++) set(lx, top + i, lz, chorusPlant);
      set(lx, top + h + 1, lz, chorusFlower);
      // A single side branch part-way up, when it fits inside the chunk.
      if (h >= 3 && rng.nextInt(2) === 0) {
        const dx = rng.nextInt(2) === 0 ? 1 : -1;
        const blx = lx + dx;
        if (blx >= 0 && blx < WORLD.SECTION) {
          const by = top + h - 1;
          set(blx, by, lz, chorusPlant);
          set(blx, by + 1, lz, chorusFlower);
        }
      }
    }
  }

  /** Stamp the parts of the obsidian pillar ring that fall in this chunk. */
  private raisePillars(cx: number, cz: number, set: SetCell): void {
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    for (const p of this.pillars) {
      const reach = p.radius + 1;
      if (p.x + reach < bx || p.x - reach >= bx + WORLD.SECTION) continue;
      if (p.z + reach < bz || p.z - reach >= bz + WORLD.SECTION) continue;
      for (let dz = -p.radius; dz <= p.radius; dz++) {
        for (let dx = -p.radius; dx <= p.radius; dx++) {
          if (dx * dx + dz * dz > p.radius * p.radius) continue;
          const lx = p.x + dx - bx, lz = p.z + dz - bz;
          if (lx < 0 || lx >= WORLD.SECTION || lz < 0 || lz >= WORLD.SECTION) continue;
          for (let y = END.ISLAND_TOP; y <= p.topY; y++) set(lx, y, lz, this.ids.obsidian);
        }
      }
      // The crystal sits on the pillar's centre column, one above its cap.
      const clx = p.x - bx, clz = p.z - bz;
      if (this.ids.endCrystal !== undefined && clx >= 0 && clx < WORLD.SECTION && clz >= 0 && clz < WORLD.SECTION) {
        set(clx, p.topY + 1, clz, this.ids.endCrystal);
      }
    }
  }

  /** True when the column is void (nothing generated) — used by spawn placement. */
  isVoid(x: number, z: number): boolean {
    return this.biomeAt(x, z) === 'the_void';
  }
}

/** The obsidian arrival platform an inbound traveller lands on (E-end): a small
 *  slab of obsidian with the air above it cleared, built where the platform sits
 *  regardless of what generated there. Pure: it writes through a block accessor. */
export function buildEndPlatform(
  access: { get(x: number, y: number, z: number): number; set(x: number, y: number, z: number, s: number): void },
  obsidian: number,
  cx: number = END.PLATFORM_X,
  cy: number = END.PLATFORM_Y,
  cz: number = END.PLATFORM_Z,
): { arrival: [number, number, number] } {
  const r = END.PLATFORM_RADIUS;
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      access.set(cx + dx, cy, cz + dz, obsidian);
      for (let dy = 1; dy <= END.PLATFORM_HEADROOM; dy++) access.set(cx + dx, cy + dy, cz + dz, STATE_AIR);
    }
  }
  return { arrival: [cx + 0.5, cy + 1, cz + 0.5] };
}
